import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import { AppErrorBoundary } from "./components/ErrorBoundary";
import { TooltipProvider } from "./components/ui/tooltip";
import { initTheme } from "./lib/theme";
import { routeTree } from "./routeTree.gen";
import "./lib/i18n";
import "./lib/ag-grid-setup";
import "@fontsource-variable/inter";
import "@fontsource-variable/ibm-plex-sans";
import "@fontsource-variable/jetbrains-mono";
import "./index.css";

// Lazy-load Sentry to keep it off the critical path
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
	import("@sentry/react").then((Sentry) => {
		Sentry.init({
			dsn: SENTRY_DSN,
			environment:
				(import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
				import.meta.env.MODE,
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.replayIntegration(),
			],
			tracesSampleRate: 1.0,
			replaysSessionSampleRate: 0.1,
			replaysOnErrorSampleRate: 1.0,
			tracePropagationTargets: ["localhost", /\.malevich\.ai/],
			sendDefaultPii: false,
		});
	});
}

initTheme();

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			retry: 1,
		},
	},
});

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

createRoot(document.getElementById("root")!).render(
	<AppErrorBoundary>
		<QueryClientProvider client={queryClient}>
			<TooltipProvider>
				<RouterProvider router={router} />
				<Toaster position="bottom-right" closeButton />
			</TooltipProvider>
		</QueryClientProvider>
	</AppErrorBoundary>,
);
